<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

withDefaults(defineProps<{ title?: string; wide?: boolean }>(), { wide: false });

const emit = defineEmits<{ close: [] }>();

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') emit('close');
}

// Achado de auditoria (fidelidade-visual-web): sem isto, o conteúdo atrás do modal ainda rola em
// touch — o scrim cobre visualmente, mas não trava o scroll do documento. Guarda o valor anterior
// (não força `''`) para não pisar em outro `overflow` que já estivesse definido.
let previousBodyOverflow: string | null = null;

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
});
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = previousBodyOverflow ?? '';
});
</script>

<template>
  <Teleport to="body">
    <div
      class="dt-dialog-scrim"
      @click.self="$emit('close')"
    >
      <div
        class="dt-dialog"
        :class="{ 'dt-dialog--wide': wide }"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <header
          v-if="title"
          class="dt-dialog__head"
        >
          <h2 class="dt-dialog__title">
            {{ title }}
          </h2>
        </header>
        <div class="dt-dialog__body">
          <slot />
        </div>
        <footer
          v-if="$slots.actions"
          class="dt-dialog__actions"
        >
          <slot name="actions" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.dt-dialog-scrim {
  position: fixed;
  inset: 0;
  background: var(--dt-dialog-scrim-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--dt-dialog-scrim-padding);
  z-index: var(--dt-z-dialog);
  animation: dt-dialog-fade var(--dt-motion-base) var(--dt-motion-ease);
}

.dt-dialog {
  background: var(--dt-surface);
  border-radius: var(--dt-dialog-radius);
  border: var(--dt-border-hairline) solid var(--dt-divider); /* .dlg desktop do protótipo (sem sombra) */
  width: 100%;
  max-width: var(--dt-dialog-max-width);
  max-height: var(--dt-dialog-max-height);
  overflow-y: auto;
  animation: dt-dialog-rise 0.22s var(--dt-motion-ease); /* .dlg do protótipo (translateY 18px, .22s) */
}

.dt-dialog--wide {
  max-width: var(--dt-dialog-max-width-wide);
}

/* Desktop sem animação de subida — styles.css:160 (.app.d .dlg { animation: none }). */
@media (min-width: 601px) {
  .dt-dialog { animation: none; }
}

.dt-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--dt-dialog-head-padding);
}

.dt-dialog__title {
  margin: 0;
  font-size: var(--dt-dialog-title-size);
  font-weight: var(--dt-dialog-title-weight);
  color: var(--dt-text);
}

.dt-dialog__body {
  padding: var(--dt-dialog-body-padding);
}

.dt-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--dt-dialog-actions-gap);
  padding: var(--dt-dialog-actions-padding);
}

@keyframes dt-dialog-fade {
  from {
    opacity: 0;
  }
}

@keyframes dt-dialog-rise {
  from {
    opacity: 0;
    transform: translateY(18px); /* styles.css:161 (.dlg sobe 18px) */
  }
}

/* Abaixo do breakpoint `sm` o diálogo vira bottom sheet — medida de layout, não token de tema. */
@media (max-width: 600px) {
  .dt-dialog-scrim {
    align-items: flex-end;
    padding: 0;
  }
  .dt-dialog {
    max-width: none;
    border-radius: 18px 18px 0 0; /* .dlg sheet do protótipo (styles.css:159) */
    border: none; /* o sheet mobile do protótipo não tem borda (styles.css:159) */
    max-height: var(--dt-dialog-sheet-max-height);
  }
}
</style>
